import { memo } from 'react';

import { GraphProvider, GraphProviderProps } from '@spazious/dollhouse-fsetup';
import { ApolloProvider, ApolloProviderProps } from '@spazious/dollhouse-graphql/apollo';
import { StoreProvider, StoreProviderProps } from '@spazious/dollhouse-store';
import { CustomThemeConfig, ThemeProvider } from '@spazious/dollhouse-theme';

import { EventManagerProvider, EventManagerProviderProps } from '../EventManager/index.js';
import { LanguageProvider } from '../LanguageProvider/index.js';
import { MainProvider, MainProviderProps } from '../Main/index.js';

export interface AppProviderProps<TCache = unknown> {
  store: StoreProviderProps['store'];
  events: EventManagerProviderProps['events'];
  client: ApolloProviderProps<TCache>['client'];
  graph: GraphProviderProps['graph'];
  main?: MainProviderProps['main'];
  customTheme?: CustomThemeConfig;
  children?: React.ReactNode;
}

/**
 * AppProvider is a wrapper component that provides the application context to its children.
 */
export const AppProvider = memo(
  <TCache,>({
    children,
    client,
    customTheme,
    events,
    graph,
    main,
    store,
  }: AppProviderProps<TCache>) => {
    return (
      <ApolloProvider client={client}>
        <EventManagerProvider events={events}>
          <StoreProvider store={store}>
            <GraphProvider graph={graph}>
              <ThemeProvider customTheme={customTheme}>
                <LanguageProvider>
                  <MainProvider main={main}>{children}</MainProvider>
                </LanguageProvider>
              </ThemeProvider>
            </GraphProvider>
          </StoreProvider>
        </EventManagerProvider>
      </ApolloProvider>
    );
  }
);
